import { useState } from 'react';

import { Collapsible, CollapsibleContent } from '@monksflow/monks-ui';
import type { AgentTreeNodeDto } from '@inference.engine/api-client/web';
import type { WorkspaceResponseDto } from '@monksflow/platform-api-sdk';

import type { CategoryId } from '@/shared/constants/category-colors';
import type { ToolConfigScope } from '@/features/admin/model/useAgentConfig/useAgentConfig';

import { AgentNodeCard } from './AgentNodeCard';

/**
 * The elbow connecting a child to its parent is drawn with two pseudo-elements
 * on the child: a vertical rail hung off the left gutter and a horizontal stub
 * reaching the card. The last child stops its rail short so the line ends at
 * the elbow instead of running past it.
 */
const CONNECTOR_CLASSES =
  "relative mt-3 before:absolute before:-left-4 before:-top-1.5 before:w-px before:bg-border before:content-[''] after:absolute after:-left-4 after:top-6 after:h-px after:w-4 after:bg-border after:content-['']";

export function AgentTreeView({
  node,
  toolConfigScopes,
  allWorkspaces = [],
  onConfigure,
  tag,
  isRoot = true,
  isLast = false,
}: {
  node: AgentTreeNodeDto;
  toolConfigScopes?: Map<string, ToolConfigScope>;
  allWorkspaces?: WorkspaceResponseDto[];
  onConfigure: (toolId: string, ownerAgentId: string) => void;
  tag: CategoryId;
  isRoot?: boolean;
  isLast?: boolean;
}) {
  const [childrenExpanded, setChildrenExpanded] = useState(isRoot);
  const hasChildren = node.children.length > 0;

  // reused at 2 call sites (isRoot and non-root branches) — computed once
  // as a plain value rather than a `renderChildren`-style closure.
  const childrenNode = hasChildren ? (
    <Collapsible open={childrenExpanded}>
      <CollapsibleContent>
        <div className="relative pl-8">
          {node.children.map((child, i) => (
            <AgentTreeView
              key={child.agentId}
              node={child}
              toolConfigScopes={toolConfigScopes}
              allWorkspaces={allWorkspaces}
              onConfigure={onConfigure}
              tag={tag}
              isRoot={false}
              isLast={i === node.children.length - 1}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  ) : null;

  if (isRoot) {
    return (
      <div>
        <AgentNodeCard
          node={node}
          toolConfigScopes={toolConfigScopes}
          allWorkspaces={allWorkspaces}
          isRoot
          onConfigure={onConfigure}
          hasChildren={hasChildren}
          childrenExpanded={childrenExpanded}
          onToggleChildren={() => setChildrenExpanded(!childrenExpanded)}
          tag={tag}
        />
        {childrenNode}
      </div>
    );
  }

  return (
    <div
      className={`${CONNECTOR_CLASSES} ${isLast ? 'before:h-7.5' : 'before:h-[calc(100%+6px)]'}`}
    >
      <AgentNodeCard
        node={node}
        toolConfigScopes={toolConfigScopes}
        isRoot={false}
        onConfigure={onConfigure}
        hasChildren={hasChildren}
        childrenExpanded={childrenExpanded}
        onToggleChildren={() => setChildrenExpanded(!childrenExpanded)}
        tag={tag}
      />
      {childrenNode}
    </div>
  );
}
